// Phase 3 — Auto-PR review panel shown inside the AdminFeedback drawer
// for feedback records that have an associated AutoPR. Fetches its own
// data so the parent admin page doesn't need to change shape.
//
// Approve/Reject buttons hit superadmin-only endpoints. The component
// quietly hides itself if no AutoPR exists for this feedback.
import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { apiGet, apiPost } from '../lib/api.js';

const STATUS_COLORS = {
  drafting: '#7C3AED',
  open:     '#3B82F6',
  approved: '#3B82F6',
  merged:   '#10b981',
  rejected: '#888',
  failed:   '#E11D48',
};

function fmtDate(d) {
  if (!d) return '—';
  return new Date(d).toISOString().slice(0, 16).replace('T', ' ');
}

export default function AutoPRPanel({ feedbackId, onChanged }) {
  const [item, setItem] = useState(null);
  const [loading, setLoading] = useState(false);
  const [acting, setActing] = useState(false);
  const [rejectReason, setRejectReason] = useState('');

  async function load() {
    if (!feedbackId) return;
    setLoading(true);
    try {
      const r = await apiGet(`/auto-pr/by-feedback/${feedbackId}`);
      setItem(r.item || null);
    } catch (err) {
      // Surface only true errors — no auto-PR for the record is fine.
      if (err?.response?.status && err.response.status !== 404) {
        toast.error(err.message || 'Failed to load auto-PR');
      }
      setItem(null);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [feedbackId]);

  if (!feedbackId) return null;
  if (loading) {
    return (
      <div className="mt-4 p-3 rounded-sm text-xs text-gray-500" style={{ background: '#13132a', border: '1px solid #252550' }}>
        Loading auto-PR…
      </div>
    );
  }
  if (!item) return null;

  const color = STATUS_COLORS[item.status] || '#7C3AED';
  const canAct = item.status === 'open';

  async function approve() {
    if (!confirm('Approve and merge this auto-PR? This will merge to main and trigger a deploy.')) return;
    setActing(true);
    try {
      await apiPost(`/feedback/${feedbackId}/auto-pr/approve`, {});
      toast.success('Approved & merged');
      await load();
      onChanged?.();
    } catch (err) {
      toast.error(err.message || 'Approve failed');
    } finally {
      setActing(false);
    }
  }
  async function reject() {
    if (!confirm('Reject and close this auto-PR? The PR will be closed without merging.')) return;
    setActing(true);
    try {
      await apiPost(`/feedback/${feedbackId}/auto-pr/reject`, { reason: rejectReason });
      toast.success('Rejected & closed');
      setRejectReason('');
      await load();
      onChanged?.();
    } catch (err) {
      toast.error(err.message || 'Reject failed');
    } finally {
      setActing(false);
    }
  }

  return (
    <div className="mt-4 p-3 rounded-sm" style={{ background: '#13132a', borderLeft: `3px solid ${color}` }}>
      <div className="flex items-center justify-between">
        <div className="text-xs uppercase tracking-wider" style={{ color: '#E040FB' }}>Auto-PR</div>
        <span className="text-[11px] px-2 py-0.5 rounded-sm uppercase" style={{ background: color, color: '#fff' }}>
          {item.status}
        </span>
      </div>

      <div className="mt-2 text-sm text-gray-200">
        Category: <strong>{item.category || '—'}</strong>
        {item.prNumber ? (
          <>
            {' · '}
            <a href={item.prUrl || '#'} target="_blank" rel="noreferrer" style={{ color: '#3B82F6' }}>
              PR #{item.prNumber}
            </a>
          </>
        ) : null}
      </div>

      <div className="mt-1 text-xs text-gray-400">
        {(item.linesChanged ?? 0)} lines · {(item.filesChanged?.length ?? 0)} file(s)
        {item.draftedAt ? ` · drafted ${fmtDate(item.draftedAt)}` : ''}
        {item.mergedAt ? ` · merged ${fmtDate(item.mergedAt)}` : ''}
      </div>

      {item.filesChanged?.length ? (
        <details className="mt-2">
          <summary className="text-xs text-gray-400 cursor-pointer">Files ({item.filesChanged.length})</summary>
          <pre className="text-[11px] mt-1 whitespace-pre-wrap" style={{ color: '#9ad' }}>
            {item.filesChanged.join('\n')}
          </pre>
        </details>
      ) : null}

      {item.failureReason ? (
        <div className="mt-2 text-xs" style={{ color: '#E11D48' }}>
          <strong>Failure:</strong> {item.failureReason}
        </div>
      ) : null}

      {item.rejectionReason ? (
        <div className="mt-2 text-xs text-gray-400">
          <strong>Rejection reason:</strong> {item.rejectionReason}
        </div>
      ) : null}

      {(item.auditLog || []).length > 0 && (
        <details className="mt-2">
          <summary className="text-xs text-gray-400 cursor-pointer">Audit log ({item.auditLog.length})</summary>
          <pre className="text-[11px] text-gray-400 whitespace-pre-wrap mt-1">
            {item.auditLog.map((e) => `${fmtDate(e.at)} · ${e.actor} · ${e.action}${e.detail ? ` — ${e.detail}` : ''}`).join('\n')}
          </pre>
        </details>
      )}

      {canAct && (
        <div className="mt-3">
          <textarea
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            rows={2}
            placeholder="Reject reason (optional)…"
            className="w-full px-2 py-1 rounded-sm text-sm"
            style={{ background: '#0A0A0F', color: '#fff', border: '1px solid #252550' }}
          />
          <div className="flex gap-2 mt-2">
            <a
              href={item.prUrl || '#'}
              target="_blank"
              rel="noreferrer"
              className="px-3 py-1 text-xs rounded-sm"
              style={{ background: '#13132a', color: '#9ad', border: '1px solid #252550' }}
            >
              View PR
            </a>
            <button
              onClick={approve}
              disabled={acting}
              className="px-3 py-1 text-xs rounded-sm font-semibold"
              style={{ background: '#7C3AED', color: '#fff', opacity: acting ? 0.6 : 1 }}
            >
              Approve & Merge
            </button>
            <button
              onClick={reject}
              disabled={acting}
              className="px-3 py-1 text-xs rounded-sm"
              style={{ background: '#13132a', color: '#E11D48', border: '1px solid #252550', opacity: acting ? 0.6 : 1 }}
            >
              Reject
            </button>
          </div>
          <div className="mt-2 text-[11px] text-gray-500">
            Approve will merge to main and trigger Render deploy. Validation re-runs at merge time.
          </div>
        </div>
      )}
    </div>
  );
}
